import React, { useState } from 'react'
import {
  alpha,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  AccessTimeOutlined as TimeIcon,
  ExpandLessRounded as CollapseIcon,
  ExpandMoreRounded as ExpandIcon,
  HubOutlined as ChainIcon,
  ReceiptLongOutlined as TransactionIcon,
  RefreshRounded as RefreshIcon,
} from '@mui/icons-material'
import tibcosService from '../services/tibcosService'
import { blockchainValueToNumber } from '../utils/blockchainMonitor'
import { useBlockchainMonitor } from './BlockchainMonitorProvider'

const formatHash = (hash?: string, head = 10, tail = 8) => {
  if (!hash) return '—'
  return hash.length > head + tail + 3 ? `${hash.slice(0, head)}…${hash.slice(-tail)}` : hash
}

const normalizeTimestamp = (value: unknown): number => {
  const raw = blockchainValueToNumber(value)
  if (!raw) return 0
  return raw < 1_000_000_000_000 ? raw * 1000 : raw
}

const formatTime = (value: unknown) => {
  const timestamp = normalizeTimestamp(value)
  return timestamp ? new Date(timestamp).toLocaleTimeString('zh-CN', { hour12: false }) : '—'
}

const Metric: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', lineHeight: 1.25 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={750}
      noWrap
      sx={{
        mt: 0.3,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
      }}
    >
      {value}
    </Typography>
  </Box>
)

export interface BlockchainStatusDockProps {
  accentColor?: string
  leftOffset?: number
}

const BlockchainStatusDock: React.FC<BlockchainStatusDockProps> = ({
  accentColor = '#1976d2',
  leftOffset = 0,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)
  const [transactionsOpen, setTransactionsOpen] = useState(false)
  const {
    connected,
    loading,
    refreshing,
    latestBlockNumber,
    chainVersion,
    latestBlock,
    recentTransactions,
    lastUpdatedAt,
    newBlockAt,
    error,
    refresh,
  } = useBlockchainMonitor()

  const transactions = Array.isArray(latestBlock?.transactions) ? latestBlock.transactions : []
  const parentHash = latestBlock?.parentInfo?.[0]?.blockHash ?? latestBlock?.parentHash
  const isNewBlock = newBlockAt !== null && Date.now() - newBlockAt < 3000
  const accessRestricted = error === '登录后可查看链上状态'
  const statusLabel = loading
    ? '正在连接'
    : connected
      ? '网络正常'
      : accessRestricted
        ? '登录后可监测'
        : '连接中断'

  return (
    <Paper
      component="aside"
      aria-label="实时区块链状态"
      square
      elevation={12}
      sx={{
        position: 'fixed',
        zIndex: (t) => t.zIndex.drawer + 3,
        left: { xs: 0, md: `${leftOffset}px` },
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        borderTop: `1px solid ${alpha(accentColor, 0.35)}`,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.96 : 0.94),
        backdropFilter: 'blur(20px) saturate(145%)',
        transition: theme.transitions.create('left', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setExpanded((current) => !current)
          }
        }}
        sx={{
          minHeight: 58,
          px: { xs: 1.5, sm: 2.25 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: alpha(accentColor, 0.035) },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 2,
            color: accentColor,
            bgcolor: alpha(accentColor, 0.1),
            border: `1px solid ${alpha(accentColor, 0.18)}`,
            flexShrink: 0,
          }}
        >
          <ChainIcon fontSize="small" />
        </Box>

        <Box sx={{ minWidth: 84 }}>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor:
                  loading || accessRestricted
                    ? 'warning.main'
                    : connected
                      ? 'success.main'
                      : 'error.main',
                boxShadow: connected
                  ? `0 0 0 4px ${alpha(theme.palette.success.main, 0.12)}`
                  : undefined,
              }}
            />
            <Typography variant="caption" fontWeight={800}>
              {statusLabel}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            TIBCOS 链上观察窗
          </Typography>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ my: 1.2, display: { xs: 'none', sm: 'block' } }}
        />

        <Stack
          direction="row"
          spacing={{ xs: 1.4, md: 3 }}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        >
          <Metric label="最新高度" value={latestBlockNumber.toLocaleString('zh-CN')} mono />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Metric label="区块时间" value={formatTime(latestBlock?.timestamp)} mono />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Metric label="区块交易" value={`${transactions.length} 笔`} />
          </Box>
          <Box sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 150 }}>
            <Metric label="区块哈希" value={formatHash(latestBlock?.hash)} mono />
          </Box>
          {isNewBlock && <Chip size="small" label="新区块" color="info" variant="outlined" />}
        </Stack>

        <Tooltip title="立即刷新">
          <span>
            <IconButton
              size="small"
              disabled={refreshing}
              onClick={(event) => {
                event.stopPropagation()
                void refresh()
              }}
              aria-label="立即刷新区块链状态"
            >
              <RefreshIcon
                fontSize="small"
                sx={{ animation: refreshing ? 'kxdex-chain-spin 900ms linear infinite' : 'none' }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" aria-label={expanded ? '收起区块链状态窗' : '展开区块链状态窗'}>
          {expanded ? <ExpandIcon /> : <CollapseIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            maxHeight: { xs: '58vh', md: 340 },
            overflowY: 'auto',
            background: `linear-gradient(135deg, ${alpha(accentColor, 0.04)}, transparent 45%)`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: { xs: 1.5, md: 2.5 },
            }}
          >
            <Metric label="区块高度" value={latestBlockNumber.toLocaleString('zh-CN')} mono />
            <Metric label="时间戳" value={formatTime(latestBlock?.timestamp)} mono />
            <Metric label="Gas 使用量" value={String(latestBlock?.gasUsed ?? '—')} mono />
            <Metric label="交易数量" value={transactions.length} />
            <Metric label="出块节点" value={`节点 ${latestBlock?.sealer ?? '—'}`} />
            <Metric label="区块版本" value={String(latestBlock?.version ?? '—')} mono />
            <Metric
              label="Chain / Group"
              value={`${tibcosService.getConfig().chainId} / ${tibcosService.getConfig().groupId}`}
              mono
            />
            <Metric label="SDK" value={chainVersion || '—'} />
          </Box>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Metric label="区块哈希" value={formatHash(latestBlock?.hash, 18, 12)} mono />
            <Metric label="父区块哈希" value={formatHash(parentHash, 18, 12)} mono />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={transactionsOpen}
              onClick={() => setTransactionsOpen((current) => !current)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setTransactionsOpen((current) => !current)
                }
              }}
              sx={{
                minHeight: 38,
                px: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 2,
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <TransactionIcon sx={{ fontSize: 18, color: accentColor }} />
              <Typography variant="body2" fontWeight={750}>
                最近交易
              </Typography>
              <Chip size="small" label={recentTransactions.length} variant="outlined" />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 'auto', display: { xs: 'none', sm: 'block' } }}
              >
                最近 {Math.min(6, Math.max(1, recentTransactions.length))} 个区块 · 默认折叠
              </Typography>
              {transactionsOpen ? (
                <ExpandIcon fontSize="small" />
              ) : (
                <CollapseIcon fontSize="small" />
              )}
            </Box>
            <Collapse in={transactionsOpen}>
              <Stack spacing={0.75} sx={{ pt: 1 }}>
                {recentTransactions.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
                    最近区块暂无交易
                  </Typography>
                ) : (
                  recentTransactions.slice(0, 8).map((transaction) => (
                    <Box
                      key={`${transaction.blockNumber}-${transaction.index}-${transaction.hash}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '72px minmax(0, 1fr)',
                          md: '80px minmax(160px, 0.9fr) minmax(220px, 1.5fr) 90px',
                        },
                        gap: 1.25,
                        alignItems: 'center',
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.action.hover, 0.55),
                      }}
                    >
                      <Typography variant="caption" fontWeight={800} color="primary">
                        #{transaction.blockNumber}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ fontFamily: 'monospace' }}>
                        {formatHash(transaction.hash, 12, 8)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: { xs: 'none', md: 'block' }, fontFamily: 'monospace' }}
                      >
                        {formatHash(transaction.from, 8, 6)} →{' '}
                        {transaction.to ? formatHash(transaction.to, 8, 6) : '合约创建'}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ display: { xs: 'none', md: 'flex' } }}
                      >
                        <TimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(transaction.blockTimestamp)}
                        </Typography>
                      </Stack>
                    </Box>
                  ))
                )}
              </Stack>
            </Collapse>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {lastUpdatedAt
              ? `状态刷新于 ${new Date(lastUpdatedAt).toLocaleTimeString('zh-CN', { hour12: false })}`
              : '正在建立链上连接'}
          </Typography>
        </Box>
      </Collapse>

      <style>{`@keyframes kxdex-chain-spin { to { transform: rotate(360deg); } }`}</style>
    </Paper>
  )
}

export default BlockchainStatusDock
